import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, Check, Lock, ArrowRight, ShieldCheck, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Cold-outreach landing page — `/welkom/:mailId`.
 *
 * Conversion-optimised single-step signup for prospects who clicked our
 * outreach email. Name + company + email are pre-filled from the lead row
 * (server-side via `/api/public/welcome/:mailId`), so the makelaar only has
 * to choose a password. That cuts ~95% of registration friction vs. the
 * default register form (6 fields → 1).
 *
 * Localised (nl/fr/en/es) via the lead's `language` — the page renders in
 * the same language as the outreach mail the prospect clicked.
 */
const COPY = {
    nl: {
        eyebrow: "Welkomstcadeau · 10 credits",
        heading: (n) => (n ? `Welkom ${n} 👋` : "Welkom op het platform 👋"),
        heading_credits_a: "je 10 credits",
        heading_credits_b: " staan klaar.",
        sub_a: "Zonder creditcard. Zonder abonnement.",
        sub_for: "Speciaal voor",
        sub_b: "Eén klik en je staat in de tool — bewerk je eerste foto in onder 30 seconden.",
        bullets: [
            "AI-retouch voor jouw eigen vastgoedfoto's — opruimen, zonnig maken, planten, restyle",
            "Upscale naar 4K-kwaliteit voor brochures en landingspagina's",
            "Omlijnen & blurren — perceelafbakening, privacy-zones",
            "Originele compositie blijft 100% bewaard — geen vergrote ramen of muren erbij",
        ],
        chip_card: "Geen creditcard",
        chip_sub: "Geen abonnement",
        chip_use: "Direct gebruiken",
        form_eyebrow: "Eén stap · 30 seconden",
        form_title: "Activeer je account.",
        form_sub: "Eén klik en je bent binnen. Wachtwoord kiezen mag, hoeft niet.",
        label_name: "Naam",
        label_company: "Kantoor",
        ph_company: "bv. Vosters Makelaardij",
        label_email: "E-mail",
        ph_email: "jij@kantoor.be",
        email_help: "Dit wordt je login-adres. Pas aan als de bovenstaande niet juist is.",
        label_password: "Kies een wachtwoord (optioneel)",
        ph_password: "Laat leeg — wij regelen het",
        submit: "Claim mijn 10 credits",
        submitting: "Bezig…",
        toast_no_pw: "Je bent ingelogd! Stel later een wachtwoord in via 'Wachtwoord vergeten' op de loginpagina.",
        terms_a: "Door verder te gaan ga je akkoord met de",
        terms_link: "algemene voorwaarden",
        terms_b: "Wij sturen geen reclame — alleen relevante product-updates.",
        already_q: "Heb je al een account?",
        already_login: "Inloggen",
        already_title: "Je hebt al een account bij ons.",
        already_body_pre: "Log in met",
        already_body_post: "om je credits en tools te gebruiken.",
        already_body_plain: "Log in om je credits en tools te gebruiken.",
        already_cta: "Inloggen",
        toast_pw: "Wachtwoord moet minstens 6 tekens zijn.",
        toast_linked: "Account herkend — je bent ingelogd!",
        toast_ready: "Je 10 gratis credits staan klaar 🎉",
        toast_fail: "Kon account niet aanmaken — probeer opnieuw.",
    },
    fr: {
        eyebrow: "Cadeau de bienvenue · 10 crédits",
        heading: (n) => (n ? `Bienvenue ${n} 👋` : "Bienvenue sur la plateforme 👋"),
        heading_credits_a: "vos 10 crédits",
        heading_credits_b: " vous attendent.",
        sub_a: "Sans carte bancaire. Sans abonnement.",
        sub_for: "Spécialement pour",
        sub_b: "Un clic et vous êtes dans l'outil — retouchez votre première photo en moins de 30 secondes.",
        bullets: [
            "Retouche IA pour vos propres photos immobilières — désencombrer, ensoleiller, plantes, restyle",
            "Upscale en qualité 4K pour brochures et pages d'atterrissage",
            "Contour & floutage — délimitation de parcelle, zones privées",
            "La composition originale reste 100% intacte — pas de fenêtres ou murs ajoutés",
        ],
        chip_card: "Sans carte bancaire",
        chip_sub: "Sans abonnement",
        chip_use: "Utilisable immédiatement",
        form_eyebrow: "Une étape · 30 secondes",
        form_title: "Activez votre compte.",
        form_sub: "Un clic et vous êtes connecté. Le mot de passe est facultatif.",
        label_name: "Nom",
        label_company: "Agence",
        ph_company: "p. ex. Immo Dupont",
        label_email: "E-mail",
        ph_email: "vous@agence.be",
        email_help: "Ceci sera votre adresse de connexion. Modifiez-la si celle ci-dessus n'est pas correcte.",
        label_password: "Choisissez un mot de passe (facultatif)",
        ph_password: "Laissez vide — on s'en occupe",
        submit: "Réclamer mes 10 crédits",
        submitting: "En cours…",
        toast_no_pw: "Vous êtes connecté ! Définissez un mot de passe plus tard via « Mot de passe oublié ».",
        terms_a: "En continuant, vous acceptez les",
        terms_link: "conditions générales",
        terms_b: "Nous n'envoyons pas de publicité — uniquement des mises à jour pertinentes.",
        already_q: "Vous avez déjà un compte ?",
        already_login: "Se connecter",
        already_title: "Vous avez déjà un compte chez nous.",
        already_body_pre: "Connectez-vous avec",
        already_body_post: "pour utiliser vos crédits et outils.",
        already_body_plain: "Connectez-vous pour utiliser vos crédits et outils.",
        already_cta: "Se connecter",
        toast_pw: "Le mot de passe doit comporter au moins 6 caractères.",
        toast_linked: "Compte reconnu — vous êtes connecté !",
        toast_ready: "Vos 10 crédits gratuits sont prêts 🎉",
        toast_fail: "Impossible de créer le compte — réessayez.",
    },
    en: {
        eyebrow: "Welcome gift · 10 credits",
        heading: (n) => (n ? `Welcome ${n} 👋` : "Welcome to the platform 👋"),
        heading_credits_a: "your 10 credits",
        heading_credits_b: " are ready.",
        sub_a: "No credit card. No subscription.",
        sub_for: "Especially for",
        sub_b: "One click and you're in the tool — edit your first photo in under 30 seconds.",
        bullets: [
            "AI retouch for your own real-estate photos — declutter, sunny sky, plants, restyle",
            "Upscale to 4K quality for brochures and landing pages",
            "Outline & blur — plot demarcation, privacy zones",
            "Original composition stays 100% intact — no enlarged windows or added walls",
        ],
        chip_card: "No credit card",
        chip_sub: "No subscription",
        chip_use: "Use instantly",
        form_eyebrow: "One step · 30 seconds",
        form_title: "Activate your account.",
        form_sub: "One click and you're in. Choosing a password is optional.",
        label_name: "Name",
        label_company: "Agency",
        ph_company: "e.g. Smith Realty",
        label_email: "Email",
        ph_email: "you@agency.com",
        email_help: "This becomes your login address. Change it if the one above isn't right.",
        label_password: "Choose a password (optional)",
        ph_password: "Leave empty — we'll handle it",
        submit: "Claim my 10 credits",
        submitting: "Working…",
        toast_no_pw: "You're logged in! Set a password later via 'Forgot password' on the login page.",
        terms_a: "By continuing you agree to the",
        terms_link: "terms & conditions",
        terms_b: "We don't send ads — only relevant product updates.",
        already_q: "Already have an account?",
        already_login: "Log in",
        already_title: "You already have an account with us.",
        already_body_pre: "Log in with",
        already_body_post: "to use your credits and tools.",
        already_body_plain: "Log in to use your credits and tools.",
        already_cta: "Log in",
        toast_pw: "Password must be at least 6 characters.",
        toast_linked: "Account recognised — you're logged in!",
        toast_ready: "Your 10 free credits are ready 🎉",
        toast_fail: "Could not create account — try again.",
    },
    es: {
        eyebrow: "Regalo de bienvenida · 10 créditos",
        heading: (n) => (n ? `Bienvenido ${n} 👋` : "Bienvenido a la plataforma 👋"),
        heading_credits_a: "tus 10 créditos",
        heading_credits_b: " te esperan.",
        sub_a: "Sin tarjeta de crédito. Sin suscripción.",
        sub_for: "Especialmente para",
        sub_b: "Un clic y estás en la herramienta — retoca tu primera foto en menos de 30 segundos.",
        bullets: [
            "Retoque IA para tus propias fotos inmobiliarias — ordenar, cielo soleado, plantas, redecorar",
            "Upscale a calidad 4K para folletos y páginas de aterrizaje",
            "Contorno y difuminado — delimitación de parcela, zonas privadas",
            "La composición original se mantiene 100% intacta — sin ventanas ampliadas ni muros añadidos",
        ],
        chip_card: "Sin tarjeta de crédito",
        chip_sub: "Sin suscripción",
        chip_use: "Uso inmediato",
        form_eyebrow: "Un paso · 30 segundos",
        form_title: "Activa tu cuenta.",
        form_sub: "Un clic y estás dentro. La contraseña es opcional.",
        label_name: "Nombre",
        label_company: "Agencia",
        ph_company: "p. ej. Inmo García",
        label_email: "Correo electrónico",
        ph_email: "tu@agencia.es",
        email_help: "Esta será tu dirección de acceso. Cámbiala si la de arriba no es correcta.",
        label_password: "Elige una contraseña (opcional)",
        ph_password: "Déjala vacía — nosotros nos encargamos",
        submit: "Reclamar mis 10 créditos",
        submitting: "Procesando…",
        toast_no_pw: "¡Has iniciado sesión! Establece una contraseña más tarde con 'Olvidé mi contraseña'.",
        terms_a: "Al continuar aceptas los",
        terms_link: "términos y condiciones",
        terms_b: "No enviamos publicidad — solo actualizaciones relevantes del producto.",
        already_q: "¿Ya tienes cuenta?",
        already_login: "Iniciar sesión",
        already_title: "Ya tienes una cuenta con nosotros.",
        already_body_pre: "Inicia sesión con",
        already_body_post: "para usar tus créditos y herramientas.",
        already_body_plain: "Inicia sesión para usar tus créditos y herramientas.",
        already_cta: "Iniciar sesión",
        toast_pw: "La contraseña debe tener al menos 6 caracteres.",
        toast_linked: "Cuenta reconocida — ¡has iniciado sesión!",
        toast_ready: "Tus 10 créditos gratis están listos 🎉",
        toast_fail: "No se pudo crear la cuenta — inténtalo de nuevo.",
    },
};

// Fotograaf-specifieke overrides (super clean onboarding voor vastgoedfotografen)
const PHOTOG_COPY = {
    nl: {
        eyebrow: "Voor vastgoedfotografen · 10 credits",
        sub_b: "Upload een reeks, vink de foto's aan en bewerk ze in bulk — download alles in één ZIP.",
        bullets: [
            "Bulk AI-retouche: vink 5+ foto's aan, één klik op 'Opruimen' — allemaal tegelijk klaar",
            "Rommel weg, grijze luchten zonnig, gazon groen, virtual staging voor lege ruimtes",
            "Download alles in één ZIP — AI-versies én originelen, netjes in mapjes",
            "Compositie blijft 100% bewaard — geen vervormde ramen of muren. 1 credit per foto",
        ],
        label_company: "Studio / bedrijfsnaam",
        ph_company: "bv. Studio Lens & Licht",
        form_title: "Activeer je fotograaf-account.",
    },
    fr: {
        eyebrow: "Pour photographes immobiliers · 10 crédits",
        sub_b: "Téléchargez une série, cochez les photos et retouchez-les en masse — téléchargez tout en un ZIP.",
        bullets: [
            "Retouche IA en masse : cochez 5+ photos, un clic sur « Désencombrer » — toutes prêtes en même temps",
            "Désordre supprimé, ciels gris ensoleillés, pelouses vertes, home staging virtuel",
            "Téléchargez tout en un ZIP — versions IA et originaux, bien classés",
            "La composition reste 100% intacte — pas de fenêtres déformées. 1 crédit par photo",
        ],
        label_company: "Studio / nom d'entreprise",
        ph_company: "p. ex. Studio Lumière",
        form_title: "Activez votre compte photographe.",
    },
};

export default function WelcomeForLead() {
    const { mailId } = useParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [info, setInfo] = useState({ valid: false, email: "", name: "", company: "", language: "nl", already_registered: false });
    const [password, setPassword] = useState("");
    // Editable overrides — pre-filled from the lead row but the prospect can
    // correct them (the lead's email might be a generic info@ inbox or a
    // typo from the scraper). The CHOSEN email becomes the real account
    // login.
    const [companyOverride, setCompanyOverride] = useState("");
    const [emailOverride, setEmailOverride] = useState("");

    const isPhotog = info.lead_type === "photographer";
    const base = COPY[info.language] || COPY.nl;
    const t = isPhotog ? { ...base, ...(PHOTOG_COPY[info.language] || PHOTOG_COPY.nl) } : base;

    /* Lookup lead defaults */
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get(`/public/welcome/${mailId}`);
                if (!mounted) return;
                setInfo(data);
                setCompanyOverride(data.company || "");
                setEmailOverride(data.email || "");
            } catch (err) {
                if (mounted) setInfo({ valid: false, email: "", name: "", company: "", language: "nl", already_registered: false });
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [mailId]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password && password.length < 6) {
            toast.error(t.toast_pw);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/public/welcome/${mailId}/register`, {
                password: password || undefined,
                company: companyOverride.trim() || undefined,
                email: emailOverride.trim() || undefined,
            });
            // Persist auth + redirect into the AI Retouch tool with welcome banner.
            localStorage.setItem("token", data.token);
            api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
            if (setUser) setUser(data.user);
            toast.success(data.linked_existing ? t.toast_linked : t.toast_ready);
            if (data.password_autogenerated && t.toast_no_pw) toast.info(t.toast_no_pw, { duration: 9000 });
            navigate("/tools/photo-retouch?welcome=1", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || t.toast_fail);
        } finally {
            setSubmitting(false);
        }
    };

    const firstName = (info.name || "").trim().split(/\s+/)[0] || "";
    const heading = t.heading(firstName);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
        );
    }

    // Already-registered → quietly nudge to login (no scary error).
    if (info.already_registered) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 flex items-center justify-center p-6" data-testid="welcome-already-registered">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-md w-full bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-sm p-8 text-center"
                >
                    <div className="h-12 w-12 mx-auto rounded-full bg-[#E5A93D]/15 border border-[#E5A93D]/40 flex items-center justify-center mb-5">
                        <Check className="h-5 w-5 text-[#E5A93D]" />
                    </div>
                    <h1 className="font-display text-2xl font-bold mb-3">
                        {t.already_title}
                    </h1>
                    <p className="text-sm text-zinc-500 leading-relaxed mb-6">
                        {info.email ? <>{t.already_body_pre} <span className="text-zinc-950 font-mono">{info.email}</span> {t.already_body_post}</> : t.already_body_plain}
                    </p>
                    <Link
                        to="/login"
                        className="inline-flex items-center justify-center gap-2 h-11 w-full bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm transition active:scale-[0.98]"
                        data-testid="welcome-go-login"
                    >
                        {t.already_cta} <ArrowRight className="h-4 w-4" />
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="welcome-page">
            {/* Backdrop with light golden glow — subtle, doesn't compete with the form */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[520px] w-[1200px] max-w-full bg-[#E5A93D]/[0.08] blur-[160px] pointer-events-none" />

            <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
                {/* Brand strip */}
                <div className="flex items-center gap-3 mb-12">
                    <img src="/social-factory-logo.png" alt="Social Factory" className="h-9 w-9 object-contain invert" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Social Factory</div>
                        <div className="text-sm font-bold text-zinc-950">Vastgoed Studio Pro</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
                    {/* LEFT — Pitch */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5A93D]/40 bg-[#E5A93D]/[0.08] mb-7">
                            <Sparkles className="h-3.5 w-3.5 text-[#E5A93D]" />
                            <span className="text-[11px] uppercase tracking-[0.22em] text-[#E5A93D] font-bold">
                                {t.eyebrow}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6 text-balance" data-testid="welcome-heading">
                            {heading}
                            <br />
                            <span className="text-[#E5A93D] italic font-medium">{t.heading_credits_a}</span>{t.heading_credits_b}
                        </h1>
                        <p className="text-base text-zinc-600 leading-relaxed mb-8 max-w-lg">
                            {t.sub_a}
                            {info.company ? <> {t.sub_for} <span className="text-zinc-950 font-semibold">{info.company}</span>.</> : null} {" "}
                            {t.sub_b}
                        </p>

                        <ul className="space-y-3 mb-10">
                            {t.bullets.map((line) => (
                                <li key={line} className="flex items-start gap-3 text-sm text-zinc-600">
                                    <Check className="h-4 w-4 text-[#E5A93D] shrink-0 mt-0.5" />
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="flex flex-wrap gap-2 text-[11px] text-zinc-500">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-zinc-200 rounded-sm">
                                <ShieldCheck className="h-3 w-3" /> {t.chip_card}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-zinc-200 rounded-sm">
                                <Lock className="h-3 w-3" /> {t.chip_sub}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-zinc-200 rounded-sm">
                                <Wand2 className="h-3 w-3" /> {t.chip_use}
                            </span>
                        </div>
                    </motion.div>

                    {/* RIGHT — Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="lg:sticky lg:top-10"
                    >
                        <form
                            onSubmit={onSubmit}
                            data-testid="welcome-form"
                            className="bg-[#FFFFFF]/85 backdrop-blur border border-[#E5A93D]/35 rounded-md p-7 lg:p-8 shadow-[0_20px_60px_-30px_rgba(229,169,61,0.6)]"
                        >
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#E5A93D] font-bold mb-2">{t.form_eyebrow}</p>
                            <h2 className="font-display text-2xl font-bold tracking-tight mb-1">{t.form_title}</h2>
                            <p className="text-xs text-zinc-500 mb-6">
                                {t.form_sub}
                            </p>

                            {info.valid && (
                                <div className="mb-5 space-y-3" data-testid="welcome-lead-fields">
                                    {info.name && (
                                        <div className="flex items-center justify-between text-xs px-3.5 py-2.5 bg-white border border-zinc-200 rounded-sm">
                                            <span className="text-zinc-500">{t.label_name}</span>
                                            <span className="text-zinc-950 font-medium">{info.name}</span>
                                        </div>
                                    )}
                                    <label className="block">
                                        <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-1.5 block">
                                            {t.label_company}
                                        </span>
                                        <input
                                            type="text"
                                            value={companyOverride}
                                            onChange={(e) => setCompanyOverride(e.target.value)}
                                            data-testid="welcome-company"
                                            placeholder={t.ph_company}
                                            className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm focus:outline-none focus:border-[#E5A93D]/60 transition"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-1.5 block">
                                            {t.label_email}
                                        </span>
                                        <input
                                            type="email"
                                            value={emailOverride}
                                            onChange={(e) => setEmailOverride(e.target.value)}
                                            required
                                            autoComplete="email"
                                            data-testid="welcome-email"
                                            placeholder={t.ph_email}
                                            className="w-full h-11 bg-white border border-zinc-200 rounded-sm px-3 text-sm font-mono focus:outline-none focus:border-[#E5A93D]/60 transition"
                                        />
                                        <span className="block mt-1 text-[10px] text-zinc-500">
                                            {t.email_help}
                                        </span>
                                    </label>
                                </div>
                            )}

                            <label className="block">
                                <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2 block">
                                    {t.label_password}
                                </span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    data-testid="welcome-password"
                                    placeholder={t.ph_password}
                                    className="w-full h-12 bg-white border border-zinc-200 rounded-sm px-4 text-sm focus:outline-none focus:border-[#E5A93D]/60 transition"
                                />
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="welcome-submit"
                                className="mt-5 inline-flex items-center justify-center gap-2 w-full h-12 bg-[#E5A93D] hover:bg-[#C8922C] disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold rounded-sm transition active:scale-[0.98] tracking-wider"
                            >
                                {submitting ? (
                                    <><Loader2 className="h-4 w-4 animate-spin" /> {t.submitting}</>
                                ) : (
                                    <><Sparkles className="h-4 w-4" /> {t.submit} <ArrowRight className="h-4 w-4" /></>
                                )}
                            </button>

                            <p className="mt-4 text-[11px] text-zinc-500 text-center leading-relaxed">
                                {t.terms_a}{" "}
                                <Link to="/voorwaarden" className="text-zinc-500 hover:text-zinc-950 underline underline-offset-2">{t.terms_link}</Link>.{" "}
                                {t.terms_b}
                            </p>

                            <div className="mt-5 pt-4 border-t border-zinc-200">
                                <Link
                                    to="/login"
                                    className="text-xs text-zinc-500 hover:text-zinc-950 transition flex items-center justify-center gap-1.5"
                                    data-testid="welcome-already-link"
                                >
                                    {t.already_q} <span className="text-zinc-600 hover:text-zinc-950">{t.already_login}</span>
                                </Link>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
